import { atom, useAtom, useSetAtom } from 'jotai';
import React from 'react';

/**
 * A prompt handed to a conversation that is being opened, for its composer to pick up once it
 * mounts. Deliberately not persisted: a prompt the user never sent is not worth restoring on the
 * next load, where it would reappear with no memory of where it came from.
 */
const agentPendingPromptAtom = atom<{
    conversationId: string;
    prompt: string;
    /** Sent as a message rather than written into the composer. */
    submit?: boolean;
} | null>(null);

/**
 * Hand a prompt to a conversation about to open. See {@link useAgentPendingPrompt}.
 */
export function useSetAgentPendingPrompt() {
    return useSetAtom(agentPendingPromptAtom);
}

/**
 * The prompt waiting for this conversation, and a way to mark it taken. One-shot: the composer
 * fills itself from it once and clears it, so reopening the conversation later starts empty.
 */
export function useAgentPendingPrompt(conversationId: string): {
    pendingPrompt: string | null;
    submitPendingPrompt: boolean;
    consumePendingPrompt: () => void;
} {
    const [pending, setPending] = useAtom(agentPendingPromptAtom);
    const isForCurrentConversation = pending?.conversationId === conversationId;
    const consumePendingPrompt = React.useCallback(() => setPending(null), [setPending]);

    return {
        pendingPrompt: isForCurrentConversation ? pending.prompt : null,
        submitPendingPrompt: isForCurrentConversation && Boolean(pending.submit),
        consumePendingPrompt,
    };
}
